import moment from 'moment';

const MONTHS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

export const DAY_TYPE = Object.freeze({
  WORKING: 'working',
  DAY_OFF: 'day_off',
  VACATION: 'vacation',
  SICK_LEAVE: 'sick_leave',
});

export const DAY_TYPE_OPTIONS = [
  { value: DAY_TYPE.WORKING, label: 'Рабочий' },
  { value: DAY_TYPE.DAY_OFF, label: 'Выходной' },
  { value: DAY_TYPE.VACATION, label: 'Отпуск' },
  { value: DAY_TYPE.SICK_LEAVE, label: 'Больничный' },
];

export const dayType = (day) => day.day_type
  ?? (day.is_day_off ? DAY_TYPE.DAY_OFF : DAY_TYPE.WORKING);

export const isAbsence = (day) => [DAY_TYPE.VACATION, DAY_TYPE.SICK_LEAVE].includes(dayType(day));

export const absenceLabel = (day) => DAY_TYPE_OPTIONS
  .find((option) => option.value === dayType(day))?.label ?? '';

export const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

export const periodLabel = (item) => {
  const start = moment.parseZone(item.start_at);

  return `${MONTHS[start.month()]} ${start.year()}`;
};

export const periodOptions = (items) => items.map((item) => ({
  value: item.key,
  label: periodLabel(item),
}));

export const formatDate = (value) => (value ? moment.parseZone(value).format('DD.MM.YYYY') : '—');

export const formatDateTime = (value) => (value ? moment(value).format('DD.MM.YYYY HH:mm') : '—');

export const formatDuration = (minutes) => {
  if (!minutes) return '—';

  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;

  return rest === 0 ? `${hours} ч` : `${hours} ч ${rest} мин`;
};

export const dayNumber = (day) => Number(day.date.slice(-2));

export const shortDate = (date) => {
  const [, month, day] = date.split('-');
  const weekday = WEEKDAYS[(new Date(date).getDay() + 6) % 7];

  return `${day}.${month}, ${weekday}`;
};

export const dayText = (day) => {
  if (!day) return 'не задан';

  return day.is_day_off ? absenceLabel(day).toLowerCase() : `${day.started_at}–${day.ended_at}`;
};

export const dayHours = (day) => {
  if (!day.is_filled) return '';
  if (day.is_day_off) return absenceLabel(day);

  return `${day.started_at}–${day.ended_at}`;
};

export const scheduleTotals = (days) => days.reduce((totals, day) => {
  if (!day.is_filled) return totals;

  return {
    filled_days_count: totals.filled_days_count + 1,
    working_days_count: totals.working_days_count + (day.is_day_off ? 0 : 1),
    days_off_count: totals.days_off_count + (dayType(day) === DAY_TYPE.DAY_OFF ? 1 : 0),
    vacation_days_count: totals.vacation_days_count + (dayType(day) === DAY_TYPE.VACATION ? 1 : 0),
    sick_leave_days_count: totals.sick_leave_days_count + (dayType(day) === DAY_TYPE.SICK_LEAVE ? 1 : 0),
    working_minutes: totals.working_minutes + (day.duration_minutes ?? 0),
  };
}, {
  filled_days_count: 0,
  working_days_count: 0,
  days_off_count: 0,
  vacation_days_count: 0,
  sick_leave_days_count: 0,
  working_minutes: 0,
});

export const durationMinutes = (startedAt, endedAt) => {
  if (!startedAt || !endedAt) return null;

  const toMinutes = (time) => {
    const [hours, minutes] = time.split(':');

    return (Number(hours) * 60) + Number(minutes);
  };

  const start = toMinutes(startedAt);
  const end = toMinutes(endedAt);

  return end > start ? end - start : (end + 1440) - start;
};

export const toPayload = (days) => days
  .filter((day) => day.is_filled)
  .map((day) => ({
    date: day.date,
    day_type: dayType(day),
    is_day_off: day.is_day_off,
    started_at: day.is_day_off ? null : day.started_at,
    ended_at: day.is_day_off ? null : day.ended_at,
  }));
